import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box, Paper, Typography, IconButton, Tooltip, Button, Chip, Divider
} from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import TimerIcon from '@mui/icons-material/Timer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/**
 * Parse SRT content into caption objects with words array.
 * Each caption: { index, startMs, endMs, text, words: [{word, start, end}] }
 */
function parseSRTWithWords(srtContent, wordTimestamps) {
  if (!srtContent) return [];

  const blocks = srtContent.trim().split(/\r?\n\r?\n/);
  const captions = [];

  for (const block of blocks) {
    const lines = block.split('\n');
    if (lines.length < 3) continue;

    const index = parseInt(lines[0], 10);
    const timingMatch = lines[1].match(
      /(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/
    );
    if (!timingMatch) continue;

    const startMs =
      parseInt(timingMatch[1]) * 3600000 +
      parseInt(timingMatch[2]) * 60000 +
      parseInt(timingMatch[3]) * 1000 +
      parseInt(timingMatch[4]);
    const endMs =
      parseInt(timingMatch[5]) * 3600000 +
      parseInt(timingMatch[6]) * 60000 +
      parseInt(timingMatch[7]) * 1000 +
      parseInt(timingMatch[8]);

    const text = lines.slice(2).join('\n');

    captions.push({ index, startMs, endMs, text, words: [] });
  }

  // Sequential word assignment: each word goes to exactly one caption
  // This prevents duplications from overlapping time tolerances
  if (wordTimestamps && wordTimestamps.length > 0 && captions.length > 0) {
    let capIdx = 0;
    for (const wt of wordTimestamps) {
      const ws = typeof wt.start === 'number' ? wt.start : parseFloat(wt.start);
      const we = typeof wt.end === 'number' ? wt.end : parseFloat(wt.end);

      // Advance to the caption that best contains this word
      while (capIdx < captions.length - 1 && ws >= captions[capIdx].endMs / 1000 + 0.05) {
        capIdx++;
      }

      captions[capIdx].words.push({ word: wt.word, start: ws, end: we });
    }
  }

  return captions;
}

/**
 * Rebuild SRT string from captions array.
 */
function captionsToSRT(captions) {
  return captions
    .map((cap, i) => {
      const idx = i + 1;
      const startTime = msToSRTTime(cap.startMs);
      const endTime = msToSRTTime(cap.endMs);
      return `${idx}\n${startTime} --> ${endTime}\n${cap.text}`;
    })
    .join('\n\n');
}

function msToSRTTime(ms) {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const f = ms % 1000;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')},${String(f).padStart(3, '0')}`;
}

function formatTime(ms) {
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const f = Math.floor((ms % 1000) / 10);
  return `${m}:${String(s).padStart(2, '0')}.${String(f).padStart(2, '0')}`;
}

/**
 * WordTimingEditor — Visual editor for subtitle captions with word-level timing.
 *
 * Props:
 * - srtContent: string — Current SRT content
 * - wordTimestamps: Array — Word-level timestamps [{word, start, end}]
 * - onChange: (newSRT) => void — Called when SRT is modified
 * - currentTime: number|null — Current playback time in seconds (for highlighting)
 * - maxCharsPerLine: number — Max characters per line for normalization
 */
export default function WordTimingEditor({
  srtContent,
  wordTimestamps,
  onChange,
  currentTime = null,
  maxCharsPerLine = 42,
}) {
  const [captions, setCaptions] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(null); // { captionIdx, wordIdx }
  const [selectedCaption, setSelectedCaption] = useState(null);
  const containerRef = useRef(null);

  // Parse SRT + word timestamps into captions
  useEffect(() => {
    const parsed = parseSRTWithWords(srtContent, wordTimestamps);
    setCaptions(parsed);
  }, [srtContent, wordTimestamps]);

  // Emit SRT changes
  const emitChange = useCallback(
    (newCaptions) => {
      setCaptions(newCaptions);
      if (onChange) {
        onChange(captionsToSRT(newCaptions));
      }
    },
    [onChange]
  );

  // Determine which caption/word is currently active (for audio highlight)
  const activeHighlight = useMemo(() => {
    if (currentTime === null || currentTime === undefined) return null;
    for (let ci = 0; ci < captions.length; ci++) {
      const cap = captions[ci];
      if (currentTime * 1000 >= cap.startMs && currentTime * 1000 <= cap.endMs) {
        for (let wi = 0; wi < cap.words.length; wi++) {
          const w = cap.words[wi];
          if (currentTime >= w.start && currentTime < w.end) {
            return { captionIdx: ci, wordIdx: wi };
          }
        }
        return { captionIdx: ci, wordIdx: -1 };
      }
    }
    return null;
  }, [currentTime, captions]);

  // ── Actions ──

  // Scroll a caption into view after an action
  const scrollToCaption = useCallback((idx) => {
    setTimeout(() => {
      const el = containerRef.current?.querySelector(`[data-caption="${idx}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }, []);

  /** Split caption at cursor position */
  const splitAtCursor = useCallback(
    (captionIdx, wordIdx) => {
      const cap = captions[captionIdx];
      if (!cap || wordIdx <= 0 || wordIdx >= cap.words.length) return;

      const wordsBefore = cap.words.slice(0, wordIdx);
      const wordsAfter = cap.words.slice(wordIdx);

      const newCap1 = {
        ...cap,
        text: wordsBefore.map((w) => w.word).join(' '),
        words: wordsBefore,
        endMs: Math.round(wordsBefore[wordsBefore.length - 1].end * 1000),
      };
      const newCap2 = {
        index: cap.index + 1,
        startMs: Math.round(wordsAfter[0].start * 1000),
        endMs: cap.endMs,
        text: wordsAfter.map((w) => w.word).join(' '),
        words: wordsAfter,
      };

      const newCaptions = [...captions];
      newCaptions.splice(captionIdx, 1, newCap1, newCap2);
      emitChange(newCaptions);
      // Keep focus on the new second part
      setSelectedCaption(captionIdx + 1);
      setCursorPosition({ captionIdx: captionIdx + 1, wordIdx: 0 });
      scrollToCaption(captionIdx + 1);
    },
    [captions, emitChange, scrollToCaption]
  );

  /** Merge caption with previous */
  const mergeWithPrevious = useCallback(
    (captionIdx) => {
      if (captionIdx <= 0) return;
      const prev = captions[captionIdx - 1];
      const curr = captions[captionIdx];

      const merged = {
        ...prev,
        endMs: curr.endMs,
        text: prev.text + '\n' + curr.text,
        words: [...prev.words, ...curr.words],
      };

      const newCaptions = [...captions];
      newCaptions.splice(captionIdx - 1, 2, merged);
      emitChange(newCaptions);
      // Focus on the merged caption, cursor at where the merge happened
      setSelectedCaption(captionIdx - 1);
      setCursorPosition({ captionIdx: captionIdx - 1, wordIdx: prev.words.length });
      scrollToCaption(captionIdx - 1);
    },
    [captions, emitChange, scrollToCaption]
  );

  /** Move last word of caption to next caption */
  const moveLastWordToNext = useCallback(
    (captionIdx) => {
      const cap = captions[captionIdx];
      const next = captions[captionIdx + 1];
      if (!cap || !next || cap.words.length <= 1) return;

      const lastWord = cap.words[cap.words.length - 1];
      const newCap = {
        ...cap,
        words: cap.words.slice(0, -1),
        text: cap.words.slice(0, -1).map((w) => w.word).join(' '),
        endMs: Math.round(cap.words[cap.words.length - 2].end * 1000),
      };
      const newNext = {
        ...next,
        words: [lastWord, ...next.words],
        text: [lastWord.word, ...next.words.map((w) => w.word)].join(' '),
        startMs: Math.round(lastWord.start * 1000),
      };

      const newCaptions = [...captions];
      newCaptions.splice(captionIdx, 2, newCap, newNext);
      emitChange(newCaptions);
      // Stay on current caption
      setSelectedCaption(captionIdx);
      scrollToCaption(captionIdx);
    },
    [captions, emitChange, scrollToCaption]
  );

  /** Re-time all captions from word timestamps */
  const retimeAll = useCallback(() => {
    const newCaptions = captions.map((cap) => {
      if (cap.words.length === 0) return cap;
      return {
        ...cap,
        startMs: Math.round(cap.words[0].start * 1000),
        endMs: Math.round(cap.words[cap.words.length - 1].end * 1000),
      };
    });
    emitChange(newCaptions);
  }, [captions, emitChange]);

  /** Normalize lines (max 2 lines, respect char limits) */
  const normalizeLines = useCallback(
    (captionIdx) => {
      const cap = captions[captionIdx];
      if (!cap) return;

      const allWords = cap.text.replace(/\n/g, ' ').split(/\s+/).filter(Boolean);
      if (allWords.length === 0) return;

      const totalText = allWords.join(' ');
      let newText;

      if (totalText.length <= maxCharsPerLine) {
        newText = totalText; // Single line
      } else {
        // Split into 2 lines, roughly equal
        const mid = Math.ceil(allWords.length / 2);
        const line1 = allWords.slice(0, mid).join(' ');
        const line2 = allWords.slice(mid).join(' ');
        newText = line1 + '\n' + line2;
      }

      const newCaptions = [...captions];
      newCaptions[captionIdx] = { ...cap, text: newText };
      emitChange(newCaptions);
    },
    [captions, maxCharsPerLine, emitChange]
  );

  // ── Keyboard handling ──
  const handleKeyDown = useCallback(
    (e, captionIdx) => {
      if (e.key === 'Enter' && !e.altKey && !e.ctrlKey) {
        // Enter: split at cursor
        e.preventDefault();
        if (cursorPosition && cursorPosition.captionIdx === captionIdx) {
          splitAtCursor(captionIdx, cursorPosition.wordIdx);
        }
      } else if (e.key === 'Enter' && e.altKey) {
        // Alt+Enter: move last word to next
        e.preventDefault();
        moveLastWordToNext(captionIdx);
      } else if (e.key === 'Backspace' && cursorPosition?.wordIdx === 0) {
        // Backspace at start: merge with previous
        e.preventDefault();
        mergeWithPrevious(captionIdx);
      } else if (e.key === 'l' && e.ctrlKey) {
        // Ctrl+L: normalize lines
        e.preventDefault();
        normalizeLines(captionIdx);
      }
    },
    [cursorPosition, splitAtCursor, moveLastWordToNext, mergeWithPrevious, normalizeLines]
  );

  if (!wordTimestamps || wordTimestamps.length === 0) {
    return null;
  }

  return (
    <Box ref={containerRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Word Timing Editor
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Herbereken timing vanuit woord-timestamps">
            <Button
              size="small"
              variant="outlined"
              startIcon={<TimerIcon />}
              onClick={retimeAll}
            >
              Re-time
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Klik tussen woorden om cursor te plaatsen. Enter = split, Backspace aan begin = merge, Alt+Enter = woord verplaatsen, Ctrl+L = regels normaliseren.
      </Typography>

      <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
        {captions.map((cap, ci) => {
          const isActive =
            activeHighlight && activeHighlight.captionIdx === ci;

          return (
            <Paper
              key={ci}
              data-caption={ci}
              variant="outlined"
              tabIndex={0}
              onKeyDown={(e) => handleKeyDown(e, ci)}
              onClick={() => setSelectedCaption(ci)}
              sx={{
                p: 1.5,
                mb: 1,
                borderColor: isActive
                  ? 'primary.main'
                  : selectedCaption === ci
                  ? 'action.selected'
                  : 'divider',
                borderWidth: isActive ? 2 : 1,
                backgroundColor: isActive ? 'action.hover' : 'background.paper',
                cursor: 'text',
                transition: 'all 0.15s',
                '&:focus': {
                  outline: 'none',
                  borderColor: 'primary.main',
                },
              }}
            >
              {/* Timing header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <Chip
                  label={`#${ci + 1}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                >
                  {formatTime(cap.startMs)} → {formatTime(cap.endMs)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {ci > 0 && (
                    <Tooltip title="Merge met vorige (Backspace)">
                      <IconButton size="small" onClick={() => mergeWithPrevious(ci)}>
                        <MergeTypeIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {cap.words.length > 1 && (
                    <Tooltip title="Split bij cursor (Enter)">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const mid = Math.ceil(cap.words.length / 2);
                          splitAtCursor(ci, mid);
                        }}
                      >
                        <CallSplitIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {ci < captions.length - 1 && cap.words.length > 1 && (
                    <Tooltip title="Laatste woord → volgende (Alt+Enter)">
                      <IconButton size="small" onClick={() => moveLastWordToNext(ci)}>
                        <ArrowForwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* Words */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, lineHeight: 1.8 }}>
                {cap.words.length > 0 ? (
                  cap.words.map((w, wi) => {
                    const isHighlighted =
                      activeHighlight &&
                      activeHighlight.captionIdx === ci &&
                      activeHighlight.wordIdx === wi;
                    const isCursor =
                      cursorPosition &&
                      cursorPosition.captionIdx === ci &&
                      cursorPosition.wordIdx === wi;

                    return (
                      <React.Fragment key={wi}>
                        {/* Clickable gap (cursor position) */}
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setCursorPosition({ captionIdx: ci, wordIdx: wi });
                          }}
                          sx={{
                            width: isCursor ? 2 : 4,
                            height: 24,
                            cursor: 'text',
                            backgroundColor: isCursor ? 'primary.main' : 'transparent',
                            borderRadius: 1,
                            alignSelf: 'center',
                            transition: 'all 0.1s',
                            '&:hover': { backgroundColor: 'action.selected', width: 3 },
                          }}
                        />
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            px: 0.3,
                            py: 0.1,
                            borderRadius: 0.5,
                            backgroundColor: isHighlighted
                              ? 'warning.light'
                              : 'transparent',
                            fontWeight: isHighlighted ? 'bold' : 'normal',
                            transition: 'background-color 0.15s',
                            userSelect: 'none',
                          }}
                        >
                          {w.word}
                        </Typography>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {cap.text}
                  </Typography>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
